import React from "react";
import api from "../config/axiosConfig";

/**
 * useActivityLogs
 * Encapsulates fetching activity logs with pagination and resilience to slight API shape differences.
 */
export default function useActivityLogs({
  rfiNo,
  open,
  limit = 10,
  type = "dimensional",
}) {
  const [page, setPage] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);

  const fetchActivityLogs = React.useCallback(async () => {
    if (!rfiNo) return;
    // Only the non-dimensional flow is implemented for Activity Logs
    const normalizedType = String(type || "")
      .toLowerCase()
      .replace(/_/g, "-");
    const isNonDim =
      normalizedType === "non-dimensional" ||
      normalizedType === "non-dimentional";
    if (!isNonDim) {
      setLogs([]);
      setTotalPages(1);
      return;
    }
    try {
      setIsLoading(true);
      const res = await api.get(
        `/api/rfi-documents-v2/rfi/${rfiNo}/activity-logs?page=${page}&limit=${limit}`
      );
      const data = res?.data || {};
      // Strict mapping based on provided API contract
      const items = Array.isArray(data.activity_logs) ? data.activity_logs : [];
      const pages = Number(data.pagination?.total_pages) || 1;

      setLogs(items);
      setTotalPages(pages);
    } catch (e) {
      console.error("Failed to fetch activity logs", e);
      setLogs([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [rfiNo, page, limit, type]);

  React.useEffect(() => {
    if (open) fetchActivityLogs();
  }, [open, fetchActivityLogs]);

  // Reset pagination when switching RFI or opening the dialog to avoid stale page
  React.useEffect(() => {
    if (open) setPage(1);
  }, [rfiNo, open, type]);

  return {
    page,
    setPage,
    isLoading,
    logs,
    totalPages,
  };
}
